import { useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Typography,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
} from "@mui/icons-material";

import taskerService, {
  type TaskerTaskType,
} from "../../services/taskerService";
import taskerAuth from "../../services/taskerAuth";
import vipOnboardingValidate, {
  OnboardingValidationError,
  type ValidateStepName,
} from "../../services/vipOnboardingValidate";
import type { ProcessedFormField } from "@/types/api/formSchema";
import OnboardingStepper, {
  type OnboardingStep,
} from "./components/OnboardingStepper";
import {
  SchemaRenderer,
  type SchemaRendererHandle,
} from "./components/SchemaRenderer";
import { BranchesRepeater } from "./components/BranchesRepeater";
import { ReviewAndSubmit } from "./components/ReviewAndSubmit";
import { PanNameAutofill } from "./components/PanNameAutofill";
import { PriorReviewerRemarks } from "./reviewer/PriorReviewerRemarks";
import { stepForms } from "./reviewer/taskConfig";
import {
  extractFieldErrors,
  readErrorMessage,
  splitOffStepErrors,
} from "./utils/mapBeErrors";
import { mapBranchErrors } from "./utils/mapBranchErrors";
import { clearPersistedFormData } from "./hooks/usePersistedFormData";
import { submissionMarkerKey } from "../../services/storageKeys";
import { formatSavedAgo } from "./utils/formatting";
import type { PriorStep } from "./reviewer/priorSteps";

const TERMINAL_STATES = new Set([
  "VM_REVIEW",
  "CM_REVIEW",
  "BIZFIN_REVIEW",
  "COMPLIANCE_REVIEW",
  "APPROVED",
  "REJECTED",
  "SEND_BACK",
  "ACTIVE",
]);

const STEP_LABEL_OVERRIDES: Record<string, { long: string; short: string }> = {
  STEP_1_BUSINESS_DETAILS: { long: "Business Details", short: "Business" },
  STEP_2_BRANCHES: { long: "Branch / Facility Details", short: "Branches" },
  STEP_3_REVIEW: { long: "Review & Submit", short: "Submit" },
};

function humanizeState(state: string): string {
  return state
    .replace(/^STEP_\d+_/, "")
    .split("_")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

function stepOrderKey(state: string): number {
  return Number(state.match(/STEP_(\d+)/)?.[1] ?? 0);
}

function deriveSteps(taskType: TaskerTaskType | null | undefined): OnboardingStep[] {
  const forms = stepForms(taskType?.task_config);
  if (!forms) return [];
  return Object.keys(forms)
    .sort((a, b) => stepOrderKey(a) - stepOrderKey(b))
    .map((state) => {
      const override = STEP_LABEL_OVERRIDES[state];
      const fallback = humanizeState(state);
      return {
        id: state,
        label: override?.long ?? fallback,
        shortLabel: override?.short ?? fallback,
      };
    });
}

function orderedStepFormIds(
  forms: Record<string, string> | undefined,
): string[] {
  if (!forms) return [];
  return Object.keys(forms)
    .sort((a, b) => stepOrderKey(a) - stepOrderKey(b))
    .map((state) => forms[state]);
}

function transitionForStep(
  currentStepIndex: number,
  totalSteps: number,
): string {
  if (currentStepIndex === totalSteps - 1) return "submit_for_review";
  return `save_step_${currentStepIndex + 1}`;
}

function VendorOnboarding() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const taskId = searchParams.get("task_id") ?? searchParams.get("taskId");
  const rendererRef = useRef<SchemaRendererHandle | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [, forceTick] = useState(0);
  const [globalError, setGlobalError] = useState<string | null>(null);

  useEffect(() => {
    if (!lastSavedAt) return;
    const id = setInterval(() => forceTick((n) => n + 1), 15_000);
    return () => clearInterval(id);
  }, [lastSavedAt]);

  const taskQuery = useQuery({
    queryKey: ["tasker-task", taskId],
    queryFn: () => taskerService.getTask(taskId as string),
    enabled: Boolean(taskId),
    staleTime: Infinity,
    // Post-submit 404s are terminal — don't retry; redirect immediately.
    retry: (failureCount, err) => {
      const status = (err as { response?: { status?: number } })?.response
        ?.status;
      if (status === 404) return false;
      return failureCount < 2;
    },
    retryDelay: 0,
  });

  const taskTypeId = taskQuery.data?.task_type_id;
  const taskTypeQuery = useQuery({
    queryKey: ["tasker-task-type", taskTypeId],
    queryFn: () => taskerService.getTaskType(taskTypeId as string),
    enabled: Boolean(taskTypeId),
    staleTime: Infinity,
  });

  const currentStatus = taskQuery.data?.current_status;
  const steps = useMemo(
    () => deriveSteps(taskTypeQuery.data),
    [taskTypeQuery.data],
  );
  const currentStepIndex = useMemo(() => {
    if (!currentStatus) return -1;
    return steps.findIndex((s) => s.id === currentStatus);
  }, [steps, currentStatus]);

  const taskConfig = taskTypeQuery.data?.task_config;
  const formsByStep = useMemo(() => stepForms(taskConfig), [taskConfig]);
  const formId =
    currentStatus && formsByStep ? formsByStep[currentStatus] : undefined;

  const formSchemaQuery = useQuery({
    queryKey: ["tasker-form-schema", formId, taskId],
    queryFn: () =>
      taskerService.getFormSchema(formId as string, taskId as string),
    enabled: Boolean(formId && taskId),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  const currentStepDataQuery = useQuery({
    queryKey: ["current-step-form-data", taskId, formId],
    queryFn: async () => {
      const rows = await taskerService.searchFormData(
        taskId as string,
        formId as string,
      );
      const latest = [...rows].sort((a, b) => {
        const ta = new Date((a.created_at as string) ?? 0).getTime();
        const tb = new Date((b.created_at as string) ?? 0).getTime();
        return tb - ta;
      })[0];
      return (latest?.form_data ?? null) as Record<string, unknown> | null;
    },
    enabled: Boolean(formId && taskId),
    staleTime: 0,
    refetchOnWindowFocus: false,
  });

  const stepName = formSchemaQuery.data?.metadata?.step_name;
  const subtitle = formSchemaQuery.data?.metadata?.subtitle as
    | string
    | undefined;
  const persistKey =
    stepName && taskId ? `vo:step:${stepName}:${taskId}` : null;
  const isReviewStep = stepName === "review_submit";
  const arrayField = formSchemaQuery.data?.fields.find(
    (f) => f.type === "array",
  );

  const priorStepIds = useMemo(
    () => orderedStepFormIds(formsByStep),
    [formsByStep],
  );

  const priorFormIds = useMemo(
    () =>
      currentStepIndex > 0
        ? priorStepIds.slice(0, currentStepIndex)
        : [],
    [priorStepIds, currentStepIndex],
  );

  const priorStepsQuery = useQuery({
    queryKey: ["prior-steps", taskId, priorFormIds],
    queryFn: async () => {
      return Promise.all(
        priorFormIds.map(async (fid) => {
          const [schema, rows] = await Promise.all([
            taskerService.getFormSchema(fid, taskId as string),
            taskerService.searchFormData(taskId as string, fid),
          ]);
          // BE appends a new form_data row on every save; pick the newest.
          const latest = [...rows].sort((a, b) => {
            const ta = new Date((a.created_at as string) ?? 0).getTime();
            const tb = new Date((b.created_at as string) ?? 0).getTime();
            return tb - ta;
          })[0];
          return {
            formId: fid,
            fields: schema.fields,
            metadata: schema.metadata,
            data: (latest?.form_data ?? {}) as Record<string, unknown>,
          };
        }),
      );
    },
    enabled: Boolean(taskId && priorFormIds.length > 0),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  const priorSteps = priorStepsQuery.data;
  const branchesScope = useMemo(() => {
    if (!priorSteps || priorSteps.length < 1) return undefined;
    return { business_details: priorSteps[0].data };
  }, [priorSteps]);

  const step1InitialValues = useMemo(() => {
    if (stepName !== "business_details") return undefined;
    const requestData = taskQuery.data?.request_data as
      | { email?: string }
      | undefined;
    return requestData?.email ? { email_id: requestData.email } : undefined;
  }, [stepName, taskQuery.data]);

  useEffect(() => {
    if (!taskQuery.data || !taskId) return;
    if (TERMINAL_STATES.has(taskQuery.data.current_status)) {
      navigate(
        `/vendor-onboarding/status?task_id=${encodeURIComponent(taskId)}`,
        { replace: true },
      );
    }
  }, [taskQuery.data, taskId, navigate]);

  const handleSchemaSubmit = async (form_data: Record<string, unknown>) => {
    if (!formId || !taskId || currentStepIndex < 0 || steps.length === 0) {
      return;
    }
    setSubmitting(true);
    setGlobalError(null);
    rendererRef.current?.setRowErrors?.({});
    rendererRef.current?.setSectionError?.(null);
    try {
      if (
        stepName === "business_details" ||
        stepName === "branches" ||
        stepName === "review_submit"
      ) {
        await vipOnboardingValidate.validateStep(
          stepName as ValidateStepName,
          taskId,
          form_data,
        );
      }
      if (isReviewStep) {
        await taskerService.saveStepFormData(formId, taskId, form_data);
      } else {
        await taskerService.upsertStepFormData(formId, taskId, form_data);
      }
      const action = transitionForStep(currentStepIndex, steps.length);
      await taskerService.executeTransition(taskId, action);
      clearPersistedFormData(persistKey);
      if (action === "submit_for_review") {
        try {
          localStorage.setItem(
            submissionMarkerKey(taskId),
            JSON.stringify({
              taskVisibleId: taskQuery.data?.task_visible_id,
              submittedAt: new Date().toISOString(),
              mobile: taskerAuth.getVendorMobile(),
            }),
          );
        } catch {
          /* localStorage disabled — Status still renders */
        }
        // Bust before navigate — stale cache bounces Status → wizard → loop.
        await queryClient.invalidateQueries({
          queryKey: ["tasker-task", taskId],
        });
        navigate(
          `/vendor-onboarding/status?task_id=${encodeURIComponent(taskId)}`,
          { replace: true },
        );
        return;
      }
      await queryClient.invalidateQueries({
        queryKey: ["tasker-task", taskId],
      });
      await queryClient.invalidateQueries({
        queryKey: ["current-step-form-data", taskId],
      });
      // Prior-step scope feeds JEXL on later steps — re-saving busts the cache.
      await queryClient.invalidateQueries({
        queryKey: ["prior-steps", taskId],
      });
    } catch (err) {
      if (err instanceof OnboardingValidationError) {
        const { fieldErrors, rowErrors, sectionError } = mapBranchErrors(
          err.errors,
          stepName === "branches" ? { defaultBranchIndex: 0 } : {},
        );
        const { onStep, offStep } = isReviewStep
          ? splitOffStepErrors(
              fieldErrors,
              (formSchemaQuery.data?.fields ?? []).map((f) => f.name),
            )
          : { onStep: fieldErrors, offStep: [] };
        rendererRef.current?.setFieldErrors(onStep);
        rendererRef.current?.setRowErrors?.(rowErrors);
        rendererRef.current?.setSectionError?.(sectionError);
        if (offStep.length > 0) {
          setGlobalError(offStep.join(" "));
        } else if (
          Object.keys(onStep).length === 0 &&
          Object.keys(rowErrors).length === 0 &&
          !sectionError
        ) {
          setGlobalError(
            readErrorMessage(
              err,
              "We couldn't save this step. Please try again.",
            ),
          );
        }
      } else {
        const fieldErrors = extractFieldErrors(err);
        if (fieldErrors) {
          rendererRef.current?.setFieldErrors(fieldErrors);
        } else {
          setGlobalError(
            readErrorMessage(
              err,
              "We couldn't save this step. Please try again.",
            ),
          );
        }
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleNext = () => {
    setGlobalError(null);
    rendererRef.current?.submit();
  };

  const handleSave = async () => {
    if (!formId || !taskId) return;
    const values = rendererRef.current?.getValues();
    if (!values) return;
    setGlobalError(null);
    setSaving(true);
    try {
      await taskerService.upsertStepFormData(formId, taskId, values);
      setLastSavedAt(new Date());
      rendererRef.current?.markSaved();
      clearPersistedFormData(persistKey);
    } catch (err) {
      setGlobalError(readErrorMessage(err, "Couldn't save. Try again."));
    } finally {
      setSaving(false);
    }
  };

  const handleBack = async () => {
    if (!taskId || currentStepIndex <= 0) return;
    setGlobalError(null);
    setSubmitting(true);
    try {
      const action = `back_to_step_${currentStepIndex}`;
      await taskerService.executeTransition(taskId, action);
      await queryClient.invalidateQueries({
        queryKey: ["tasker-task", taskId],
      });
      await queryClient.invalidateQueries({
        queryKey: ["current-step-form-data", taskId],
      });
      await queryClient.invalidateQueries({
        queryKey: ["prior-steps", taskId],
      });
    } catch (err) {
      setGlobalError(
        readErrorMessage(err, "Couldn't go back to the previous step."),
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (!taskId) {
    return (
      <Box sx={{ maxWidth: 720, mx: "auto", mt: 8, p: 3 }}>
        <Alert severity="error">
          Missing task_id in the URL. Please use the invitation link sent to
          your email.
        </Alert>
      </Box>
    );
  }

  if (taskQuery.isInitialLoading || taskTypeQuery.isInitialLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (taskQuery.isError) {
    const status = (taskQuery.error as { response?: { status?: number } })
      ?.response?.status;
    if (status === 404 && taskId) {
      return (
        <Navigate
          to={`/vendor-onboarding/status?task_id=${encodeURIComponent(taskId)}`}
          replace
        />
      );
    }
    return (
      <Box sx={{ maxWidth: 720, mx: "auto", mt: 8, p: 3 }}>
        <Alert severity="error">
          Failed to load your task:{" "}
          {taskQuery.error instanceof Error
            ? taskQuery.error.message
            : "Unknown error"}
        </Alert>
      </Box>
    );
  }

  if (taskTypeQuery.isError) {
    return (
      <Box sx={{ maxWidth: 720, mx: "auto", mt: 8, p: 3 }}>
        <Alert severity="error">
          Failed to load task configuration:{" "}
          {taskTypeQuery.error instanceof Error
            ? taskTypeQuery.error.message
            : "Unknown error"}
        </Alert>
      </Box>
    );
  }

  if (!taskQuery.data) {
    return (
      <Box sx={{ maxWidth: 720, mx: "auto", mt: 8, p: 3 }}>
        <Alert severity="error">Task not found.</Alert>
      </Box>
    );
  }

  if (TERMINAL_STATES.has(taskQuery.data.current_status)) {
    return null;
  }

  if (!formId || currentStepIndex < 0) {
    return (
      <Box sx={{ maxWidth: 720, mx: "auto", mt: 8, p: 3 }}>
        <Alert severity="error">
          This task isn&apos;t ready to be filled out yet (state:{" "}
          {taskQuery.data.current_status}). Please check back later or contact
          support.
        </Alert>
      </Box>
    );
  }

  if (formSchemaQuery.isInitialLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (formSchemaQuery.isError || !formSchemaQuery.data) {
    return (
      <Box sx={{ maxWidth: 720, mx: "auto", mt: 8, p: 3 }}>
        <Alert severity="error">
          Failed to load the form. Please refresh the page.
        </Alert>
      </Box>
    );
  }

  const shownStep = steps[currentStepIndex];
  const isLastStep = currentStepIndex === steps.length - 1;
  const nextStepLabel = steps[currentStepIndex + 1]?.label;
  const nextLabel = isLastStep
    ? "Submit for Review"
    : nextStepLabel
      ? `Next: ${nextStepLabel}`
      : "Save & Continue";
  const canGoBack = currentStepIndex > 0;
  const savedFormData = currentStepDataQuery.data ?? null;

  const stepBody = renderStepBody({
    isReviewStep,
    arrayField,
    formSchemaData: formSchemaQuery.data,
    priorSteps,
    branchesScope,
    step1InitialValues:
      savedFormData && Object.keys(savedFormData).length > 0
        ? savedFormData
        : step1InitialValues,
    priorStepLoading:
      priorStepsQuery.isFetching && !priorStepsQuery.data,
    editingBranchInitial:
      savedFormData && Array.isArray(savedFormData.branches)
        ? (savedFormData.branches as Record<string, unknown>[])
        : undefined,
    taskId,
    rendererRef,
    persistKey,
    onSubmit: handleSchemaSubmit,
    onBranchesSubmit: (branches) =>
      handleSchemaSubmit({ branches: branches as unknown[] }),
  });

  return (
    <Box
      sx={{
        maxWidth: 1080,
        mx: "auto",
        py: 4,
        px: 3,
        "& .MuiInputBase-input": { fontSize: 14 },
        "& .MuiInputBase-root": { fontSize: 14 },
      }}
    >
      <Typography
        gutterBottom
        sx={{ fontSize: 22, fontWeight: 700, color: "text.primary" }}
      >
        Vendor Onboarding
      </Typography>
      <Typography variant="body2" sx={{ mb: 4, color: "text.secondary" }}>
        Welcome. You have been invited to complete your vendor onboarding.
        Please fill in all required information. Your application will be
        reviewed by the Vendor Manager team.
      </Typography>

      <OnboardingStepper steps={steps} currentStep={currentStepIndex} />

      <Paper
        elevation={0}
        sx={{
          mt: 4,
          p: 0,
          border: 1,
          borderColor: "#E0E4EF",
          borderRadius: "12px",
          bgcolor: "background.paper",
          boxShadow:
            "0 1px 3px rgba(0,0,0,0.05), 0 4px 16px rgba(0,0,0,0.06)",
          overflow: "hidden",
        }}
      >
        <Box sx={{ px: 4, py: 3 }}>
          <Typography sx={{ fontSize: 16, fontWeight: 700 }}>
            Step {currentStepIndex + 1} — {shownStep.label}
          </Typography>
          {subtitle && (
            <Typography
              variant="body2"
              sx={{ mt: 0.75, color: "text.secondary", fontSize: 13 }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
        <Divider />

        <Box sx={{ px: 4, py: 3 }}>
          {globalError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {globalError}
            </Alert>
          )}
          {taskQuery.data && (
            <PriorReviewerRemarks
              task={taskQuery.data}
              taskConfig={taskTypeQuery.data?.task_config}
              authMode="vendor"
            />
          )}
          {stepBody}
        </Box>
        <Divider />

        <Box
          sx={{
            px: 4,
            py: 3,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Box>
            {canGoBack && (
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={handleBack}
                disabled={submitting || saving}
                sx={{
                  borderColor: "#E0E4EF",
                  color: "text.primary",
                  textTransform: "none",
                  fontWeight: 600,
                  "&:hover": { borderColor: "#C7CDDF", bgcolor: "#F5F7FC" },
                }}
              >
                Back
              </Button>
            )}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {lastSavedAt && (
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", fontSize: 12 }}
              >
                Saved · {formatSavedAgo(lastSavedAt)}
              </Typography>
            )}
            {!isReviewStep && (
              <Button
                variant="outlined"
                onClick={handleSave}
                disabled={submitting || saving}
                sx={{
                  borderColor: "#E0E4EF",
                  color: "text.primary",
                  textTransform: "none",
                  fontWeight: 600,
                  "&:hover": { borderColor: "#C7CDDF", bgcolor: "#F5F7FC" },
                }}
              >
                {saving ? "Saving…" : "Save"}
              </Button>
            )}
          <Button
            variant="contained"
            onClick={handleNext}
            endIcon={!isLastStep && !submitting ? <ArrowForwardIcon /> : undefined}
            disabled={submitting || saving}
            sx={
              isLastStep
                ? {
                    bgcolor: "#1B7C4E",
                    "&:hover": { bgcolor: "#166240" },
                    "&.Mui-disabled": {
                      bgcolor: "#A8C8B5",
                      color: "#FFFFFF",
                    },
                  }
                : undefined
            }
          >
            {submitting ? "Saving…" : nextLabel}
          </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}

type StepBodyArgs = {
  isReviewStep: boolean;
  arrayField: ProcessedFormField | undefined;
  formSchemaData: {
    fields: ProcessedFormField[];
    scope?: Record<string, unknown>;
  };
  priorSteps: PriorStep[] | undefined;
  branchesScope: Record<string, unknown> | undefined;
  step1InitialValues: Record<string, unknown> | undefined;
  editingBranchInitial: Record<string, unknown>[] | undefined;
  priorStepLoading: boolean;
  taskId: string | null;
  rendererRef: React.MutableRefObject<SchemaRendererHandle | null>;
  persistKey: string | null;
  onSubmit: (data: Record<string, unknown>) => void;
  onBranchesSubmit: (branches: Record<string, unknown>[]) => void;
};

function renderStepBody({
  isReviewStep,
  arrayField,
  formSchemaData,
  priorSteps,
  branchesScope,
  step1InitialValues,
  editingBranchInitial,
  priorStepLoading,
  taskId,
  rendererRef,
  persistKey,
  onSubmit,
  onBranchesSubmit,
}: StepBodyArgs) {
  if (isReviewStep) {
    const declarationField = formSchemaData.fields[0];
    if (!declarationField) {
      return (
        <Alert severity="error">
          Missing declaration field on review step.
        </Alert>
      );
    }
    if (priorStepLoading) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={24} />
        </Box>
      );
    }
    return (
      <ReviewAndSubmit
        ref={rendererRef}
        declarationField={declarationField}
        priorSteps={priorSteps ?? []}
        vendorMobile={taskerAuth.getVendorMobile()}
        onSubmit={onSubmit}
      />
    );
  }

  if (arrayField && Array.isArray(arrayField.fields)) {
    if (priorStepLoading) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={24} />
        </Box>
      );
    }
    return (
      <BranchesRepeater
        ref={rendererRef}
        branchFields={arrayField.fields as ProcessedFormField[]}
        scope={branchesScope}
        initialValues={editingBranchInitial}
        onSubmit={onBranchesSubmit}
        attachmentOwner={{ ownerRef: taskId ?? undefined, ownerType: "TASK" }}
        persistKey={persistKey}
      />
    );
  }

  return (
    <SchemaRenderer
      ref={rendererRef}
      fields={formSchemaData.fields}
      scope={formSchemaData.scope}
      initialValues={step1InitialValues}
      onSubmit={onSubmit}
      attachmentOwner={{ ownerRef: taskId ?? undefined, ownerType: "TASK" }}
      columns={1}
      persistKey={persistKey}
      formEffects={<PanNameAutofill />}
    />
  );
}

export default VendorOnboarding;
